"use client";

import {
  motion,
  useMotionValue,
  useAnimationFrame,
} from "framer-motion";
import { useState, useRef } from "react";
import everestImg from "../../assets/images/gallery/peaks/everest.jpg";
import k2Img from "../../assets/images/gallery/peaks/k2.jpg";
import kangchenjungaImg from "../../assets/images/gallery/peaks/kangchenjunga.jpg";
import lhotseImg from "../../assets/images/gallery/peaks/lhotse.jpg";
import makaluImg from "../../assets/images/gallery/peaks/makalu.jpg";
import choOyuImg from "../../assets/images/gallery/peaks/chooyu.jpg";
import dhaulagiriImg from "../../assets/images/gallery/peaks/dhaulagiri.jpg";
import manasluImg from "../../assets/images/gallery/peaks/manaslu.jpg";
import nangaParbatImg from "../../assets/images/gallery/peaks/nangaParbat.jpg";
import annapurnaImg from "../../assets/images/gallery/peaks/annapurna.jpg";
import gasherbrum1Img from "../../assets/images/gallery/peaks/gasherbrum1.jpg";
import broadPeakImg from "../../assets/images/gallery/peaks/broadpeak.jpg";
import gasherbrum2Img from "../../assets/images/gallery/peaks/gasherbrum2.jpg";
import shishapangmaImg from "../../assets/images/gallery/peaks/shishapangma.jpg";
import heroImage from "../../assets/images/hero-image-12.jpg";


const steps = [
  {
    number: "1",
    title: "Mount Everest",
    height: "8,848 m",
    description:
      "The highest mountain on Earth, standing as the ultimate test of endurance and ambition.",
    image: everestImg,
  },
  {
    number: "2",
    title: "K2",
    height: "8,611 m",
    description:
      "Known as the Savage Mountain, famed for its technical difficulty and extreme conditions.",
    image: k2Img,
  },
  {
    number: "3",
    title: "Kangchenjunga",
    height: "8,586 m",
    description:
      "A sacred peak on the Nepal–India border, revered for its beauty and remoteness.",
    image: kangchenjungaImg,
  },
  {
    number: "4",
    title: "Lhotse",
    height: "8,516 m",
    description:
      "Connected to Everest, Lhotse challenges climbers with its steep faces and couloirs.",
    image: lhotseImg,
  },
  {
    number: "5",
    title: "Makalu",
    height: "8,485 m",
    description:
      "A striking pyramid-shaped peak, demanding exceptional technical skill.",
    image: makaluImg,
  },
  {
    number: "6",
    title: "Cho Oyu",
    height: "8,188 m",
    description:
      "Considered one of the more accessible 8000ers, ideal for high-altitude progression.",
    image: choOyuImg,
  },
  {
    number: "7",
    title: "Dhaulagiri I",
    height: "8,167 m",
    description:
      "A massive Himalayan giant with steep relief and avalanche-prone slopes.",
    image: dhaulagiriImg,
  },
  {
    number: "8",
    title: "Manaslu",
    height: "8,163 m",
    description:
      "The Mountain of the Spirit, offering dramatic ridges and stunning views.",
    image: manasluImg,
  },
  {
    number: "9",
    title: "Nanga Parbat",
    height: "8,126 m",
    description:
      "The Killer Mountain, notorious for its immense Rupal Face.",
    image: nangaParbatImg,
  },
  {
    number: "10",
    title: "Annapurna I",
    height: "8,091 m",
    description:
      "One of the deadliest peaks, respected for its unpredictable weather.",
    image: annapurnaImg,
  },
  {
    number: "11",
    title: "Gasherbrum I",
    height: "8,080 m",
    description:
      "Hidden Peak, remote and elegant, requiring serious expedition commitment.",
    image: gasherbrum1Img,
  },
  {
    number: "12",
    title: "Broad Peak",
    height: "8,051 m",
    description:
      "A vast summit ridge offering long, demanding ascents in the Karakoram.",
    image: broadPeakImg,
  },
  {
    number: "13",
    title: "Gasherbrum II",
    height: "8,035 m",
    description:
      "A classic high-altitude climb with breathtaking Karakoram scenery.",
    image: gasherbrum2Img,
  },
  {
    number: "14",
    title: "Shishapangma",
    height: "8,027 m",
    description:
      "The only 8000er entirely in Tibet, often chosen as a first 8000m ascent.",
    image: shishapangmaImg,
  },
];

export default function HorizontalInfiniteBelt() {
  const loopedSteps = [...steps, ...steps];
  const [activeImage, setActiveImage] = useState(null);

  /* 🔥 REAL SPEED CONTROL */
  const x = useMotionValue(0);
  const beltRef = useRef(null);
  const [speed] = useState(50); // px/sec (desktop default)

  /* 🎯 Seamless marquee loop */
  useAnimationFrame((_, delta) => {
    const moveBy = (speed * delta) / 1000;
    x.set(x.get() - moveBy);

    if (beltRef.current) {
      const halfWidth = beltRef.current.scrollWidth / 2;
      if (Math.abs(x.get()) >= halfWidth) {
        x.set(0);
      }
    }
  });

  return (
    <>
      <section className="relative w-full py-24 px-6 bg-gray-900 overflow-hidden"
       style={{
              backgroundImage: `url(${heroImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
          >
            {/* DARK OVERLAY (background only) */}
            <div className="absolute inset-0 bg-[#0e202b] opacity-80 z-10" />
      
            {/* CONTENT WRAPPER (IMPORTANT) */}
            <div className="relative z-20">
              
        {/* Section Header */}
        <div className="max-w-7xl mx-auto px-6 text-center mb-20">
          <p className="uppercase tracking-[0.3em] text-sm text-gray-300">
            The Ultimate High-Altitude Challenge
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-white mt-4">
            The 14 Eight-Thousanders
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto mt-4">
            Earth’s highest and most formidable peaks, where endurance, skill,
            and human spirit are tested at the edge of possibility.
          </p>
        </div>
        <motion.div
          ref={beltRef}
          className="flex gap-8 flex-nowrap"
          style={{ x, willChange: "transform" }}
        >
          {loopedSteps.map((step, index) => (
            <div
              key={index}
              className="relative flex-shrink-0 w-[400px] p-12
                         bg-white/5 backdrop-blur-md shadow-lg rounded-[48px]
                         overflow-hidden"
            >
              {/* WATERMARK TEXT */}
              <h3 className="absolute inset-0 text-[120px] md:text-[160px] font-bold
                             text-gray-300/10 leading-none select-none
                             flex items-center justify-center text-center">
                {step.title.toUpperCase()}
                <br />
                {step.title.toUpperCase()}
              </h3>

              {/* ICY HOVER IMAGE */}
           <div
              className="relative z-10 w-full h-[250px] rounded-xl mb-4 overflow-hidden
                        cursor-pointer group"
              onClick={() => setActiveImage(step.image)}
            >
              {/* IMAGE */}
              <img
                src={step.image}
                alt={step.title}
                className="w-full h-full object-cover
                          transition-all duration-500 ease-out
                          group-hover:scale-[1.03]
                          group-hover:blur-[2px]
                          group-hover:brightness-110
                          group-hover:saturate-90"
              />

              {/* DARK OVERLAY */}
              <div
                className="absolute inset-0 bg-black/40
                          transition-opacity duration-500
                          group-hover:bg-black/20"
              />
            </div>

              <div className="relative z-10 text-white">
                <div className="text-[64px] font-bold mb-2">{step.number}</div>
                <h3 className="text-xl font-semibold">{step.title}</h3>
                <p className="text-sm text-gray-400 mb-2">{step.height}</p>
                <p className="text-gray-200 text-sm">{step.description}</p>
              </div>
            </div>
          ))}
        </motion.div>
            </div>
      </section>

        {activeImage && (
        <div
          className="fixed inset-0 z-[9998] bg-black/80 backdrop-blur-sm
                    flex items-center justify-center"
          onClick={() => setActiveImage(null)}
        >
          <div
            className="relative z-[9999] max-w-[90vw] max-h-[90vh]
                      flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setActiveImage(null)}
              className="absolute top-4 right-4 text-white text-2xl z-10
                        bg-black/40 hover:bg-black/60
                        rounded-full w-10 h-10
                        flex items-center justify-center
                        backdrop-blur"
            >
              ✕
            </button>


            <img
              src={activeImage}
              alt="Expanded"
              className="max-w-full max-h-[90vh]
                        object-contain
                        rounded-2xl shadow-2xl"
            />
          </div>
        </div>
      )}

    </>
  );
}
